import { createContext, useContext, useMemo, useRef, useState } from 'react'
/**
 * A field name, in either register.
 *
 * A string rather than the two unions, because a per-person field has to be
 * named with the person to be unique: "P2:birth_year" is a different thing
 * being typed into than "P1:birth_year", and a union of field names cannot say
 * so. The business app had one subject and did not have to.
 */
type FactField = string

/**
 * Which answer somebody is in the middle of entering.
 *
 * The record is append-only, so a value written to it is written for good.
 * That is the right property for an audit trail and it makes a half-typed
 * number permanent: the autosave runs on a nine hundred millisecond pause, and
 * a person entering a revenue figure they have to go and look up will pause for
 * longer than that in the middle of it.
 *
 * It is not hypothetical. The demo record holds this, three seconds apart:
 *
 *     09:56:39  maximum_single_shipment  7
 *     09:56:42  maximum_single_shipment  70000
 *
 * The seven was recorded as a fact and judged by the rules. The live answer was
 * never wrong, because materialising takes the newest and the record corrected
 * itself three seconds later. What it cost is the timeline: `shouldAppend`
 * starts a new point when there is no open row, so the first write of a session
 * becomes a snapshot, and everything after it falls inside the session gap. A
 * session's only recorded point can be a number nobody finished typing.
 *
 * Longer debouncing does not fix it, because the pause was longer than any
 * debounce worth having. Committing on blur alone does, and costs the board
 * moving as somebody types, which is a feature rather than an accident.
 *
 * So both: the value goes on screen at every keystroke and the board follows
 * it, and the record waits until the field is left. A context rather than a
 * prop because the inputs are rendered from a dozen places inside
 * QuestionField, and threading a callback to each of them would be a great deal
 * of plumbing for one boolean.
 *
 * The residual risk is a tab closed with a field still focused, which is a
 * value entered and never recorded. Browsers fire blur before unload in the
 * ordinary case, and the answer is on screen rather than lost, so the exposure
 * is a reload away from being retyped rather than work destroyed.
 */
export interface Editing {
  /** This field is being entered. Hold it back from the record. */
  begin: (field: FactField) => void
  /** Finished. It may be written, and a save is nudged so it is written now. */
  end: (field: FactField) => void
  /**
   * This field's value moved.
   *
   * Optional because the default context outside a provider does nothing, and a
   * component rendered in isolation should not have to care.
   */
  moved?: (field: FactField) => void
}

const nothing: Editing = { begin: () => {}, end: () => {} }

export const EditingContext = createContext<Editing>(nothing)

export function useEditing(): Editing {
  return useContext(EditingContext)
}

/**
 * The state behind the context, which the business app keeps inside its App.
 *
 * Lifted here rather than copied into a second App, because the reasoning is
 * subtle enough to be worth having in one readable place, and because a
 * household needs the same behaviour for a different reason: its fields are
 * per person, so two people's answers can be in flight at once and each has to
 * settle on its own.
 *
 * Focus alone was the business app's first attempt and was wrong in a way its
 * tests caught at once: a field filled and never left was withheld for ever, so
 * a value typed and then abandoned, or typed before a reload, was lost. Blur is
 * not reliable enough to be the only thing that releases a write.
 *
 * So the real signal is that the value is still moving, and focus only decides
 * how patient to be about it. A field is held back while it is focused and has
 * changed within the settle window; once it stops changing it is written
 * whether or not anybody has left it.
 */
const SETTLE_MS = 4000

export interface EditingState extends Editing {
  /** The fields to hold back from this pass, and nothing longer than it must. */
  unsettled: () => Set<string>
  /** Called when a value moves, so the window knows when it last did. */
  moved: (field: FactField) => void
}

export function useEditingState(): EditingState {
  const focused = useRef<Set<FactField>>(new Set())
  const changedAt = useRef(new Map<FactField, number>())
  const [, nudge] = useState(0)

  return useMemo(
    () => ({
      begin: (field) => {
        focused.current.add(field)
      },
      end: (field) => {
        focused.current.delete(field)
        // A save is nudged so leaving a field writes it at once, which is the
        // ordinary case and needs no waiting at all.
        nudge((n) => n + 1)
      },
      moved: (field) => {
        changedAt.current.set(field, Date.now())
      },
      unsettled: () => {
        const at = Date.now()
        const held = new Set<string>()
        for (const field of focused.current) {
          const last = changedAt.current.get(field)
          if (last !== undefined && at - last < SETTLE_MS) held.add(field)
        }
        return held
      },
    }),
    [],
  )
}
